// Per-network config resolver.
//
// Three runtime networks: `local`, `devnet`, `mainnet`. Each owns its own
// API URL, RPC URL, program ID, API key, and NATS URL — read from
// `NEXT_PUBLIC_*_LOCAL`, `_DEVNET`, `_MAINNET` env vars respectively.
//
// `local` is engineer-only — Vercel never sets `_LOCAL` env vars, so the
// network is silently unavailable in production. Engineers running
// `pnpm dev` populate `_LOCAL` vars in `.env.local` to point at a localhost
// backend; the rest of the dev loop (RPC, program ID, etc.) typically
// targets real Solana devnet so transactions land on-chain.
//
// `mainnet` reads `_MAINNET` only — there is no `_LOCAL` fallback (we don't
// pretend-mainnet against a local backend).
//
// OAuth redirect URI is intentionally NOT in this object — a single
// Vercel deployment serves both Solana networks via the cookie toggle,
// so OAuth providers (Google / Apple) only ever redirect to one
// canonical callback URL. Read `env.NEXT_PUBLIC_OAUTH_REDIRECT_URI`
// (singleton) where needed — same pattern as Turnkey config.
//
// ─── Default network ─────────────────────────────────────────────────
// Fresh visitors with no cookie land on `local` if `_LOCAL` is configured
// (engineer dev), otherwise `devnet`. Vercel deployments never see `local`
// configured, so production users always default to `devnet`.

import { networkEnv as env } from '@/lib/config/network-env';

import type { Network } from './types';

export interface NetworkConfig {
  network: Network;
  apiUrl: string;
  /** Optional — NATS WebSocket URL for the v2 SDK's realtime transport.
   *  Absent ⇒ the SDK runs REST-only (`natsUrl=''`). Per-network because
   *  devnet and mainnet have separate NATS clusters. */
  natsUrl: string | undefined;
  rpcUrl: string;
  programId: string;
  // NOTE: Turnkey ORG / API base / auth-proxy config + OAuth redirect
  // URI are NOT in this object on purpose. The auth session persists
  // when the user toggles between networks; that requires a single
  // Turnkey org / sub-org tree AND a single OAuth callback URL shared
  // across both networks. Read `env.NEXT_PUBLIC_TURNKEY_ORG_ID` and
  // `env.NEXT_PUBLIC_OAUTH_REDIRECT_URI` (singletons) where needed —
  // never per-network.
}

// Hosting UIs keep stray whitespace in pasted values; an address with a
// trailing space must not read as a different (invalid) address.
function nonEmpty(value: string | undefined): string | undefined {
  const trimmed = value?.trim();
  return trimmed ? trimmed : undefined;
}

interface PerNetworkRaw {
  apiUrl: string | undefined;
  natsUrl: string | undefined;
  rpcUrl: string | undefined;
  programId: string | undefined;
}

function rawForNetwork(network: Network): PerNetworkRaw {
  if (network === 'mainnet') {
    return {
      apiUrl: nonEmpty(env.NEXT_PUBLIC_API_URL_MAINNET),
      natsUrl: nonEmpty(env.NEXT_PUBLIC_NATS_URL_MAINNET),
      rpcUrl: nonEmpty(env.NEXT_PUBLIC_RPC_URL_MAINNET),
      programId: nonEmpty(env.NEXT_PUBLIC_PROGRAM_ID_MAINNET),
    };
  }
  if (network === 'local') {
    // `local` is "devnet at the protocol level, with a localhost backend".
    // Engineers typically only set `_LOCAL_API_URL` (their localhost
    // backend) and let RPC / program ID / API key inherit from `_DEVNET`
    // so on-chain calls still hit real Solana devnet.
    return {
      apiUrl: nonEmpty(env.NEXT_PUBLIC_API_URL_LOCAL) ?? nonEmpty(env.NEXT_PUBLIC_API_URL_DEVNET),
      natsUrl:
        nonEmpty(env.NEXT_PUBLIC_NATS_URL_LOCAL) ?? nonEmpty(env.NEXT_PUBLIC_NATS_URL_DEVNET),
      rpcUrl: nonEmpty(env.NEXT_PUBLIC_RPC_URL_LOCAL) ?? nonEmpty(env.NEXT_PUBLIC_RPC_URL_DEVNET),
      programId:
        nonEmpty(env.NEXT_PUBLIC_PROGRAM_ID_LOCAL) ?? nonEmpty(env.NEXT_PUBLIC_PROGRAM_ID_DEVNET),
    };
  }
  // devnet
  return {
    apiUrl: nonEmpty(env.NEXT_PUBLIC_API_URL_DEVNET),
    natsUrl: nonEmpty(env.NEXT_PUBLIC_NATS_URL_DEVNET),
    rpcUrl: nonEmpty(env.NEXT_PUBLIC_RPC_URL_DEVNET),
    programId: nonEmpty(env.NEXT_PUBLIC_PROGRAM_ID_DEVNET),
  };
}

/** True iff every required field (apiUrl, rpcUrl, programId) for
 *  `network` is non-empty. OAuth redirect URI isn't checked — it's a
 *  singleton (`env.NEXT_PUBLIC_OAUTH_REDIRECT_URI`) validated at boot via
 *  the env schema.
 *
 *  `local` therefore CANNOT use the generic check: `rawForNetwork('local')`
 *  falls back to the `_DEVNET` value for every single field, so on any
 *  devnet-configured deployment — including production — the generic check
 *  returns true, `getDefaultNetwork()` short-circuits to `local`, and every
 *  visitor (plus every analytics event's `network` tag) is labelled `local`
 *  instead of `devnet`. What actually defines a local environment is an
 *  operator pointing the app at a localhost backend, so that is what we
 *  require: an explicitly-set `NEXT_PUBLIC_API_URL_LOCAL`. */
export function isNetworkConfigured(network: Network): boolean {
  if (network === 'local') return nonEmpty(env.NEXT_PUBLIC_API_URL_LOCAL) !== undefined;
  const raw = rawForNetwork(network);
  return raw.apiUrl !== undefined && raw.rpcUrl !== undefined && raw.programId !== undefined;
}

/** Default network for a fresh visitor (no cookie) — also what crawlers
 *  see, since they don't carry the `NETWORK_PREFERENCE` cookie.
 *
 *  Resolution order:
 *    1. `local` configured → engineer running with `_LOCAL` env vars
 *    2. `mainnet` configured → live deployment; mainnet is the canonical
 *       user-facing surface, devnet is opt-in via toggle / share link.
 *       Crucially: this makes `/markets`, `/launchpad`, etc. INDEXABLE
 *       on mainnet — without it the middleware would 307 every fresh
 *       request to `?demo-mode` (devnet) with `noindex`, and Googlebot
 *       (which never has a cookie) would conclude the listings shouldn't
 *       be indexed.
 *    3. `devnet` fallback — pre-mainnet deployments and previews. */
export function getDefaultNetwork(): Network {
  if (isNetworkConfigured('local')) return 'local';
  if (isNetworkConfigured('mainnet')) return 'mainnet';
  return 'devnet';
}

/** True when the runtime toggle UI should be shown — at least two
 *  networks are populated. Single-network deployments hide the toggle. */
export function isToggleEnabled(): boolean {
  const configured: number = [
    isNetworkConfigured('local') ? 1 : 0,
    isNetworkConfigured('devnet') ? 1 : 0,
    isNetworkConfigured('mainnet') ? 1 : 0,
  ].reduce((a, b): number => a + b, 0);
  return configured >= 2;
}

function suffixFor(network: Network): string {
  if (network === 'mainnet') return '_MAINNET';
  if (network === 'local') return '_LOCAL';
  return '_DEVNET';
}

function require_(value: string | undefined, network: Network, field: string): string {
  if (value === undefined) {
    throw new Error(
      `getNetworkConfig('${network}') — missing NEXT_PUBLIC_${field}${suffixFor(network)}`,
    );
  }
  return value;
}

// Dev-only: `pnpm dev:mobile` proxies `/v1/*` through the page origin to the
// per-network backend (chosen by NETWORK_PREFERENCE cookie at the rewrite
// layer in next.config.ts). Without this, a cloudflared-tunneled phone
// session can't reach localhost:4000, and api-devnet.morfi.markets blocks
// the trycloudflare origin via CORS. Server-side renders keep the real
// apiUrl — Next dev can reach localhost directly.
function maybeProxyApiUrl(apiUrl: string): string {
  if (env.NEXT_PUBLIC_DEV_PROXY !== 'true') return apiUrl;
  if (typeof window === 'undefined') return apiUrl;
  return window.location.origin;
}

type DeploymentField =
  | 'MARKETS_PROGRAM_ID'
  | 'CP_SWAP_PROGRAM_ID'
  | 'QUOTE_MINT'
  | 'SELL_CAP_PROGRAM';

/** A Markets-deployment-specific input (`NEXT_PUBLIC_<FIELD>_<NETWORK>`), or
 *  undefined when unset. `local` inherits devnet's value only while it runs
 *  devnet's program: a local program is its own deployment, with its own
 *  cp-swap, quote mint and program variant. */
export function deploymentEnv(network: Network, field: DeploymentField): string | undefined {
  if (network === 'mainnet') return nonEmpty(env[`NEXT_PUBLIC_${field}_MAINNET` as const]);
  const devnet = nonEmpty(env[`NEXT_PUBLIC_${field}_DEVNET` as const]);
  if (network === 'devnet') return devnet;
  return (
    nonEmpty(env[`NEXT_PUBLIC_${field}_LOCAL` as const]) ??
    (nonEmpty(env.NEXT_PUBLIC_PROGRAM_ID_LOCAL) === undefined ? devnet : undefined)
  );
}

/** Resolve the full config for `network`. Throws if the required env
 *  vars (apiUrl / rpcUrl / programId) for that network are missing —
 *  the runtime should never reach a network whose config isn't shipped.
 *  Gate with `isNetworkConfigured(network)` before calling on a code
 *  path that might encounter an unshipped network (e.g. SEO surfaces
 *  calling `getNetworkConfig('mainnet')` pre-launch). */
export function getNetworkConfig(network: Network): NetworkConfig {
  const raw = rawForNetwork(network);
  return {
    network,
    apiUrl: maybeProxyApiUrl(require_(raw.apiUrl, network, 'API_URL')),
    natsUrl: raw.natsUrl,
    rpcUrl: require_(raw.rpcUrl, network, 'RPC_URL'),
    programId: require_(raw.programId, network, 'PROGRAM_ID'),
  };
}
